<template>
  <div class="dashboard-container">
    <!-- 新手引導 Modal -->
    <div v-if="showOnboardingModal" class="subscription-modal-overlay">
      <div class="onboarding-modal">
        <div class="onboarding-modal-header">
          <div class="onboarding-modal-title-wrap">
            <i class="ri-rocket-line"></i>
            <div>
              <h3>歡迎使用 LOCKLINE！</h3>
              <p>跟著以下步驟快速完成初始設定</p>
            </div>
          </div>
        </div>

        <div class="onboarding-modal-body">
          <div class="onboarding-steps">
            <div class="onboarding-step">
              <div class="onboarding-step-num">1</div>
              <div class="onboarding-step-content">
                <strong>綁定 LINE 官方帳號</strong>
                <span>連結您的 LINE 官方帳號，啟用推播通知與會員綁定功能。</span>
              </div>
              <button class="onboarding-step-btn" @click="goOnboardingRoute('ClientCreateLineConfig')">
                前往設定 <i class="ri-arrow-right-line"></i>
              </button>
            </div>

            <div class="onboarding-step">
              <div class="onboarding-step-num">2</div>
              <div class="onboarding-step-content">
                <strong>設定門市資訊</strong>
                <span>新增您的門市，讓會員在報到與簽到時識別正確分店。</span>
              </div>
              <button class="onboarding-step-btn" @click="goOnboardingRoute('ClientStores')">
                前往設定 <i class="ri-arrow-right-line"></i>
              </button>
            </div>

            <div class="onboarding-step">
              <div class="onboarding-step-num">3</div>
              <div class="onboarding-step-content">
                <strong>建立點數規則</strong>
                <span>設定消費積點規則，激勵會員持續回購。</span>
              </div>
              <button class="onboarding-step-btn" @click="goOnboardingRoute('PointsEarningRulesNormal')">
                前往設定 <i class="ri-arrow-right-line"></i>
              </button>
            </div>

            <div class="onboarding-step">
              <div class="onboarding-step-num">4</div>
              <div class="onboarding-step-content">
                <strong>新增優惠券</strong>
                <span>建立折扣、兌換等各式優惠券吸引會員消費。</span>
              </div>
              <button class="onboarding-step-btn" @click="goOnboardingRoute('CouponTemplates')">
                前往設定 <i class="ri-arrow-right-line"></i>
              </button>
            </div>

            <div class="onboarding-step">
              <div class="onboarding-step-num">5</div>
              <div class="onboarding-step-content">
                <strong>匯入或新增第一位會員</strong>
                <span>開始建立您的會員資料庫，累積忠實顧客。</span>
              </div>
              <button class="onboarding-step-btn" @click="goOnboardingRoute('ClientMembers')">
                前往設定 <i class="ri-arrow-right-line"></i>
              </button>
            </div>
          </div>
        </div>

        <div class="onboarding-modal-footer">
          <label class="onboarding-dismiss-check">
            <input type="checkbox" v-model="onboardingDismissChecked" />
            <span>不再顯示此說明</span>
          </label>
          <button class="onboarding-close-btn" @click="closeOnboardingModal">
            我知道了
          </button>
        </div>
      </div>
    </div>

    <!-- LINE 未設定 Popup Modal -->
    <div v-if="showLineSetupModal" class="subscription-modal-overlay" @click.self="closeLineSetupModal">
      <div class="subscription-modal line-setup-modal">
        <div class="subscription-modal-header">
          <i class="ri-line-fill" style="color: #06c755;"></i>
          <h3>尚未設定 LINE 官方帳號</h3>
          <button class="subscription-modal-close" @click="closeLineSetupModal">
            <i class="ri-close-line"></i>
          </button>
        </div>
        <div class="subscription-modal-body">
          <p>連結 LINE 官方帳號後，即可啟用以下功能：</p>
          <ul class="line-setup-modal-features">
            <li><i class="ri-megaphone-line"></i> 推播通知與廣播訊息</li>
            <li><i class="ri-user-heart-line"></i> 會員 LINE 綁定與身份識別</li>
            <li><i class="ri-layout-grid-line"></i> Rich Menu 選單設計</li>
            <li><i class="ri-qr-code-line"></i> QR Code 掃碼入會</li>
            <li><i class="ri-links-line"></i> 短網址追蹤</li>
          </ul>
          <p class="subscription-modal-hint">僅需幾分鐘即可完成設定，立即開始使用完整功能。</p>
        </div>
        <div class="subscription-modal-footer" style="flex-direction: column; align-items: stretch; gap: 12px;">
          <div style="display: flex; gap: 10px;">
            <button
              class="subscription-modal-btn subscription-modal-btn--primary"
              style="background: #06c755; flex: 1;"
              @click="handleLineSetupGo"
            >
              <i class="ri-add-circle-line"></i>
              立即設定 LINE 帳號
            </button>
            <button class="subscription-modal-btn subscription-modal-btn--secondary" @click="closeLineSetupModal">
              稍後再說
            </button>
          </div>
          <label class="onboarding-dismiss-check" style="justify-content: flex-end;">
            <input type="checkbox" v-model="lineSetupDismissChecked" />
            <span>不再顯示此提醒</span>
          </label>
        </div>
      </div>
    </div>

    <!-- 訂閱警告 Popup Modal -->
    <div v-if="showSubscriptionModal" class="subscription-modal-overlay" @click.self="closeSubscriptionModal">
      <div class="subscription-modal" :class="`subscription-modal--${subscriptionError.type}`">
        <div class="subscription-modal-header">
          <i :class="getSubscriptionIcon(subscriptionError.type)"></i>
          <h3>{{ getSubscriptionTitle(subscriptionError.type) }}</h3>
          <button class="subscription-modal-close" @click="closeSubscriptionModal">
            <i class="ri-close-line"></i>
          </button>
        </div>
        <div class="subscription-modal-body">
          <p>{{ subscriptionError.message }}</p>
          <p class="subscription-modal-hint">請聯繫管理員或前往訂閱頁面完成續費，以確保服務持續正常運作。</p>
        </div>
        <div class="subscription-modal-footer">
          <button
            v-if="subscriptionError.action === 'renew'"
            class="subscription-modal-btn subscription-modal-btn--primary"
            @click="handleModalRenew"
          >
            <i class="ri-arrow-right-circle-line"></i>
            前往訂閱頁面
          </button>
          <button class="subscription-modal-btn subscription-modal-btn--secondary" @click="closeSubscriptionModal">
            我知道了
          </button>
        </div>
      </div>
    </div>

    <!-- 頁面標題區 + 刷新按鈕 -->
    <div class="dashboard-header">
      <div class="header-left">
        <h1 class="dashboard-title">營運總覽</h1>
        <p class="dashboard-subtitle">歡迎回來，{{ (user && user.name) || (user && user.username) || '' }}</p>
        <p v-if="lastUpdated" class="last-updated">
          最後更新：{{ formatDateTime(lastUpdated) }}
          <span v-if="fromCache" class="cache-badge">快取</span>
        </p>
      </div>
      <div class="header-right">
        <!-- 訂閱到期日常態顯示（載入完成後一律顯示） -->
        <div
          v-if="subscriptionLoaded"
          class="sub-badge"
          :class="subscriptionBadgeClass"
          @click="$router.push('/client/subscription')"
          title="點擊前往訂閱頁面"
        >
          <i :class="subscriptionBadgeIcon"></i>
          <div class="sub-badge-text">
            <span class="sub-badge-label">{{ subscriptionInfo?.neverExpires ? '訂閱效期' : '訂閱到期日' }}</span>
            <span class="sub-badge-date">{{ subscriptionInfo?.neverExpires ? '永久有效' : (subscriptionInfo ? formatDate(subscriptionInfo.endDate) : '尚無訂閱') }}</span>
          </div>
          <div v-if="!subscriptionInfo?.neverExpires && subscriptionInfo && subscriptionInfo.daysRemaining !== null && subscriptionInfo.daysRemaining !== undefined" class="sub-badge-days">
            {{ subscriptionInfo.daysRemaining >= 0 ? `剩 ${subscriptionInfo.daysRemaining} 天` : '已到期' }}
          </div>
        </div>

        <!-- 試用中：立即訂閱按鈕 -->
        <button
          v-if="subscriptionLoaded && subscriptionInfo?.isTrial && !subscriptionInfo?.neverExpires"
          class="subscribe-now-btn"
          @click="$router.push('/client/subscription')"
        >
          <i class="ri-vip-crown-line"></i>
          立即訂閱
        </button>

        <button 
          class="refresh-btn" 
          @click="refreshStats" 
          :disabled="refreshing"
        >
          <i class="ri-refresh-line" :class="{ 'spinning': refreshing }"></i>
          <span>{{ refreshing ? '刷新中...' : '刷新數據' }}</span>
        </button>
      </div>
    </div>

    <!-- Loading 狀態 -->
    <div v-if="loading && !stats" class="loading-container">
      <i class="ri-loader-4-line spinning"></i>
      <p>載入中...</p>
    </div>

    <!-- 錯誤狀態 -->
    <div v-else-if="error" class="error-container">
      <i class="ri-error-warning-line"></i>
      <p>{{ error }}</p>
      <button @click="loadStats" class="retry-btn">重試</button>
    </div>

    <!-- Dashboard 內容 -->
    <div v-else-if="stats" class="dashboard-content">
      <!-- 訂閱警告 Alert Card -->
      <div v-if="subscriptionError" class="subscription-alert-card" :class="`subscription-alert-card--${subscriptionError.type}`">
        <div class="subscription-alert-card-icon">
          <i :class="getSubscriptionIcon(subscriptionError.type)"></i>
        </div>
        <div class="subscription-alert-card-content">
          <strong>{{ getSubscriptionTitle(subscriptionError.type) }}</strong>
          <span>{{ subscriptionError.message }}</span>
        </div>
        <button
          v-if="subscriptionError.action === 'renew'"
          class="subscription-alert-card-btn"
          @click="$router.push('/client/subscription')"
        >
          前往訂閱
        </button>
      </div>

      <!-- LINE 未設定提示 -->
      <div v-if="showLineSetupNotice" class="line-setup-notice">
        <div class="line-setup-notice-icon">
          <i class="ri-line-fill"></i>
        </div>
        <div class="line-setup-notice-content">
          <strong>尚未設定 LINE 官方帳號</strong>
          <span>連結 LINE 官方帳號後，即可使用推播通知、會員綁定、Rich Menu 等完整功能。</span>
        </div>
        <button
          class="line-setup-notice-btn"
          @click="$router.push({ name: 'ClientCreateLineConfig' })"
        >
          <i class="ri-add-circle-line"></i>
          立即設定
        </button>
      </div>

      <!-- 核心指標卡片區（8 KPI） -->
      <div class="stats-cards">
        <div class="stat-card">
          <div class="stat-icon" style="background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);">
            <i class="ri-user-line"></i>
          </div>
          <div class="stat-content">
            <div class="stat-title-row">
              <h3>會員總數</h3>
              <button type="button" class="kpi-help-btn" title="計算說明" @click="openKpiHelp('members')">
                <i class="ri-question-line"></i>
              </button>
            </div>
            <p class="stat-value">{{ formatNumber(stats.memberStats?.allMembers ?? stats.memberStats?.totalMembers) }}</p>
            <span class="stat-label stat-label-block">有效會員 {{ formatNumber(stats.memberStats?.totalMembers) }}</span>
            <span class="stat-label stat-label-block">
              本月新增 {{ formatNumber(stats.memberStats?.newMembersThisMonth) }}
              <span :class="getGrowthClass(stats.memberStats?.newMembersGrowthRate || 0)">
                <i :class="getGrowthIcon(stats.memberStats?.newMembersGrowthRate || 0)"></i>
                {{ formatGrowth(stats.memberStats?.newMembersGrowthRate) }}
              </span>
            </span>
            <span
              class="stat-label"
              :class="getGrowthClass(stats.memberStats?.totalMembersGrowthRate || 0)"
            >
              <i :class="getGrowthIcon(stats.memberStats?.totalMembersGrowthRate || 0)"></i>
              較上月底 {{ formatGrowth(stats.memberStats?.totalMembersGrowthRate) }}
            </span>
          </div>
        </div>

        <div class="stat-card">
          <div class="stat-icon" style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);">
            <i class="ri-user-star-line"></i>
          </div>
          <div class="stat-content">
            <div class="stat-title-row">
              <h3>活躍會員</h3>
              <button type="button" class="kpi-help-btn" title="計算說明" @click="openKpiHelp('activeMembers')">
                <i class="ri-question-line"></i>
              </button>
            </div>
            <p class="stat-value">
              {{ formatNumber(stats.memberStats?.activeMembers) }}
              <span class="stat-unit">人</span>
            </p>
            <span class="stat-label stat-label-block">
              活躍率 {{ stats.memberStats?.activeMembersRate ? (stats.memberStats.activeMembersRate * 100).toFixed(1) : 0 }}%
            </span>
            <span
              class="stat-label"
              :class="getGrowthClass(stats.memberStats?.activeMembersGrowthRate || 0)"
            >
              <i :class="getGrowthIcon(stats.memberStats?.activeMembersGrowthRate || 0)"></i>
              較上月 {{ formatGrowth(stats.memberStats?.activeMembersGrowthRate) }}
            </span>
          </div>
        </div>

        <div class="stat-card">
          <div class="stat-icon" style="background: linear-gradient(135deg, #9b59b6 0%, #8e44ad 100%);">
            <i class="ri-coupon-line"></i>
          </div>
          <div class="stat-content">
            <div class="stat-title-row">
              <h3>優惠券發行</h3>
              <button type="button" class="kpi-help-btn" title="計算說明" @click="openKpiHelp('couponsIssued')">
                <i class="ri-question-line"></i>
              </button>
            </div>
            <p class="stat-value">{{ formatNumber(stats.couponStats?.totalIssued) }}</p>
            <span class="stat-label stat-label-block">本月 {{ formatNumber(stats.couponStats?.issuedThisMonth) }}</span>
            <span
              class="stat-label"
              :class="getGrowthClass(stats.couponStats?.issuedGrowthRate || 0)"
            >
              <i :class="getGrowthIcon(stats.couponStats?.issuedGrowthRate || 0)"></i>
              較上月 {{ formatGrowth(stats.couponStats?.issuedGrowthRate) }}
            </span>
          </div>
        </div>

        <div class="stat-card">
          <div class="stat-icon" style="background: linear-gradient(135deg, #e67e22 0%, #f39c12 100%);">
            <i class="ri-checkbox-circle-line"></i>
          </div>
          <div class="stat-content">
            <div class="stat-title-row">
              <h3>優惠券核銷</h3>
              <button type="button" class="kpi-help-btn" title="計算說明" @click="openKpiHelp('couponsUsed')">
                <i class="ri-question-line"></i>
              </button>
            </div>
            <p class="stat-value">{{ formatNumber(stats.couponStats?.used) }}</p>
            <span class="stat-label stat-label-block">本月 {{ formatNumber(stats.couponStats?.usedThisMonth) }}</span>
            <span
              class="stat-label"
              :class="getGrowthClass(stats.couponStats?.usedGrowthRate || 0)"
            >
              <i :class="getGrowthIcon(stats.couponStats?.usedGrowthRate || 0)"></i>
              較上月 {{ formatGrowth(stats.couponStats?.usedGrowthRate) }}
            </span>
          </div>
        </div>

        <div class="stat-card">
          <div class="stat-icon" style="background: linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%);">
            <i class="ri-percent-line"></i>
          </div>
          <div class="stat-content">
            <div class="stat-title-row">
              <h3>核銷率</h3>
              <button type="button" class="kpi-help-btn" title="計算說明" @click="openKpiHelp('usageRate')">
                <i class="ri-question-line"></i>
              </button>
            </div>
            <p class="stat-value">{{ stats.couponStats?.usageRate || 0 }}%</p>
            <span class="stat-label stat-label-block">本月 {{ stats.couponStats?.usageRateThisMonth || 0 }}%</span>
            <span
              class="stat-label"
              :class="getGrowthClass(stats.couponStats?.usageRateGrowthRate || 0)"
            >
              <i :class="getGrowthIcon(stats.couponStats?.usageRateGrowthRate || 0)"></i>
              較上月 {{ formatGrowth(stats.couponStats?.usageRateGrowthRate) }}
            </span>
          </div>
        </div>

        <div class="stat-card">
          <div class="stat-icon" style="background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%);">
            <i class="ri-coins-line"></i>
          </div>
          <div class="stat-content">
            <div class="stat-title-row">
              <h3>發放點數</h3>
              <button type="button" class="kpi-help-btn" title="計算說明" @click="openKpiHelp('pointsIssued')">
                <i class="ri-question-line"></i>
              </button>
            </div>
            <p class="stat-value">{{ formatNumber(stats.pointsStats?.totalIssued) }}</p>
            <span class="stat-label stat-label-block">本月 {{ formatNumber(stats.pointsStats?.issuedThisMonth) }} · 餘額 {{ formatNumber(stats.pointsStats?.currentBalance) }}</span>
            <span
              class="stat-label"
              :class="getGrowthClass(stats.pointsStats?.issuedGrowthRate || 0)"
            >
              <i :class="getGrowthIcon(stats.pointsStats?.issuedGrowthRate || 0)"></i>
              較上月 {{ formatGrowth(stats.pointsStats?.issuedGrowthRate) }}
            </span>
          </div>
        </div>

        <div class="stat-card">
          <div class="stat-icon" style="background: linear-gradient(135deg, #30cfd0 0%, #330867 100%);">
            <i class="ri-exchange-dollar-line"></i>
          </div>
          <div class="stat-content">
            <div class="stat-title-row">
              <h3>消耗點數</h3>
              <button type="button" class="kpi-help-btn" title="計算說明" @click="openKpiHelp('pointsUsed')">
                <i class="ri-question-line"></i>
              </button>
            </div>
            <p class="stat-value">{{ formatNumber(stats.pointsStats?.totalUsed) }}</p>
            <span class="stat-label stat-label-block">本月 {{ formatNumber(stats.pointsStats?.usedThisMonth) }}</span>
            <span
              class="stat-label"
              :class="getGrowthClass(stats.pointsStats?.usedGrowthRate || 0)"
            >
              <i :class="getGrowthIcon(stats.pointsStats?.usedGrowthRate || 0)"></i>
              較上月 {{ formatGrowth(stats.pointsStats?.usedGrowthRate) }}
            </span>
          </div>
        </div>

        <div class="stat-card">
          <div class="stat-icon" style="background: linear-gradient(135deg, #feca57 0%, #ff9f43 100%);">
            <i class="ri-money-dollar-circle-line"></i>
          </div>
          <div class="stat-content">
            <div class="stat-title-row">
              <h3>本月營收</h3>
              <button type="button" class="kpi-help-btn" title="計算說明" @click="openKpiHelp('revenue')">
                <i class="ri-question-line"></i>
              </button>
            </div>
            <p class="stat-value">{{ formatCurrency(stats.revenueStats?.monthlyRevenue) }}</p>
            <span class="stat-label" :class="getGrowthClass(stats.revenueStats?.revenueGrowthRate || 0)">
              <i :class="getGrowthIcon(stats.revenueStats?.revenueGrowthRate || 0)"></i>
              較上月 {{ formatGrowth(stats.revenueStats?.revenueGrowthRate) }}
            </span>
          </div>
        </div>
      </div>

      <!-- KPI 計算說明 -->
      <div
        v-if="kpiHelp"
        class="subscription-modal-overlay"
        @click.self="closeKpiHelp"
      >
        <div class="subscription-modal kpi-help-modal">
          <div class="subscription-modal-header">
            <i class="ri-calculator-line" style="color: #4facfe;"></i>
            <h3>{{ kpiHelp.title }} · 計算說明</h3>
            <button type="button" class="subscription-modal-close" @click="closeKpiHelp">
              <i class="ri-close-line"></i>
            </button>
          </div>
          <div class="subscription-modal-body kpi-help-body">
            <ul class="kpi-help-list">
              <li v-for="(item, idx) in kpiHelp.items" :key="idx">
                <strong v-if="item.label">{{ item.label }}</strong>
                <span>{{ item.text }}</span>
              </li>
            </ul>
            <p v-if="kpiHelp.note" class="kpi-help-note">{{ kpiHelp.note }}</p>
          </div>
          <div class="subscription-modal-footer">
            <button type="button" class="subscription-modal-btn subscription-modal-btn--secondary" @click="closeKpiHelp">
              關閉
            </button>
          </div>
        </div>
      </div>

      <!-- 次要資訊列：今日訂單 + LINE 好友 -->
      <div class="secondary-stats-row">
        <div class="secondary-stat-chip">
          <i class="ri-shopping-cart-line"></i>
          <span>今日訂單 <strong>{{ formatNumber(stats.orderStats?.todayOrders) }}</strong></span>
          <span class="secondary-stat-muted">本月 {{ formatNumber(stats.orderStats?.monthlyOrders) }} 筆</span>
        </div>
        <div
          v-if="lineConfigs.length > 0"
          class="secondary-stat-chip line-chip"
          @click="$router.push({ name: 'ClientLineConfigList' })"
        >
          <i class="ri-line-fill"></i>
          <span>LINE 有效好友 <strong>{{ totalFollowers.toLocaleString() }}</strong></span>
          <template v-if="!lineConfigsLoading && lineConfigs.length === 1">
            <span class="secondary-stat-muted">{{ lineConfigs[0]?.configName }}</span>
            <span v-if="lineConfigs[0]?.blockRate != null" class="secondary-stat-muted line-block">
              封鎖率 {{ lineConfigs[0].blockRate }}%
            </span>
          </template>
          <template v-else-if="!lineConfigsLoading && lineConfigs.length > 1">
            <span class="secondary-stat-muted">{{ lineConfigs.length }} 個帳號</span>
          </template>
        </div>
      </div>

      <!-- 待辦提醒區 -->
      <div v-if="hasAlerts && stats" class="alert-section">
        <div v-if="stats.orderStats && stats.orderStats.pendingOrders > 0" class="alert-card warning">
          <i class="ri-alarm-warning-line"></i>
          <span>有 {{ stats.orderStats.pendingOrders }} 筆訂單待處理</span>
          <button @click="$router.push('/client/orders')" class="alert-btn">前往處理</button>
        </div>
        <div v-if="stats.couponStats && stats.couponStats.expiringSoon > 0" class="alert-card info">
          <i class="ri-time-line"></i>
          <span>有 {{ stats.couponStats.expiringSoon }} 張優惠券即將到期</span>
        </div>
        <div v-if="stats.pointsStats && stats.pointsStats.expiringIn30Days > 0" class="alert-card info">
          <i class="ri-coins-line"></i>
          <span>有 {{ formatNumber(stats.pointsStats.expiringIn30Days) }} 點數將在 30 天內到期</span>
        </div>
      </div>

      <!-- 趨勢圖表區：會員成長 + 等級分布 -->
      <div class="charts-section">
        <div class="chart-card chart-card--wide">
          <div class="chart-card-toolbar">
            <div class="period-tabs">
              <button
                v-for="opt in periodOptions"
                :key="'mg-' + opt.value"
                type="button"
                class="period-tab"
                :class="{ active: memberGrowthPeriod === opt.value }"
                @click="memberGrowthPeriod = opt.value"
              >{{ opt.label }}</button>
            </div>
          </div>
          <MultiTrendChart
            v-if="memberGrowthSeries.length > 0"
            :key="'member-growth-' + memberGrowthPeriod"
            :title="memberGrowthTitle"
            :series="memberGrowthSeries"
            height="300px"
          />
          <div v-else class="empty-state chart-empty">
            <i class="ri-user-add-line"></i>
            <p>{{ memberGrowthPeriod === 'week' && !stats?.trendData?.membersWeekly ? '週趨勢尚未產生，請點重新整理' : '暫無會員成長數據' }}</p>
          </div>
        </div>

        <div class="chart-card chart-card--side">
          <PieChart
            v-if="stats.memberStats && stats.memberStats.membersByTier && stats.memberStats.membersByTier.length > 0"
            title="會員等級分布"
            :data="stats.memberStats.membersByTier"
            nameKey="tierName"
            valueKey="memberCount"
            :radius="['42%', '62%']"
            :show-label="false"
            height="320px"
          />
          <div v-else class="empty-state chart-empty">
            <i class="ri-pie-chart-line"></i>
            <p>暫無等級數據</p>
          </div>
        </div>
      </div>

      <!-- 營運趨勢分析 -->
      <div class="charts-section charts-section--single">
        <div class="chart-card">
          <div class="chart-card-toolbar">
            <div class="period-tabs">
              <button
                v-for="opt in periodOptions"
                :key="'ops-' + opt.value"
                type="button"
                class="period-tab"
                :class="{ active: opsTrendPeriod === opt.value }"
                @click="opsTrendPeriod = opt.value"
              >{{ opt.label }}</button>
            </div>
          </div>
          <MultiTrendChart
            v-if="opsTrendSeries.length > 0"
            :key="'ops-trend-' + opsTrendPeriod"
            :title="opsTrendTitle"
            :series="opsTrendSeries"
            height="280px"
          />
          <div v-else class="empty-state chart-empty">
            <i class="ri-line-chart-line"></i>
            <p>{{ opsTrendPeriod === 'week' && !stats?.trendData?.membersWeekly ? '週趨勢尚未產生，請點重新整理' : '暫無趨勢數據' }}</p>
          </div>
        </div>
      </div>

      <!-- 等級 × 核銷率 -->
      <div class="charts-section charts-section--single">
        <div class="chart-card">
          <TierRedemptionChart
            v-if="tierRedemptionData.length > 0"
            title="等級 × 核銷率"
            :data="tierRedemptionData"
            height="300px"
          />
          <div v-else class="empty-state chart-empty">
            <i class="ri-bar-chart-grouped-line"></i>
            <p>暫無等級核銷數據</p>
          </div>
        </div>
      </div>

      <!-- 排行榜區 -->
      <div class="ranking-section">
        <div class="ranking-card">
          <h3><i class="ri-user-star-line"></i> 活躍會員 Top 10</h3>
          <div v-if="stats.rankingData && stats.rankingData.topMembers && stats.rankingData.topMembers.length > 0" class="ranking-list">
            <div 
              v-for="(member, index) in stats.rankingData.topMembers" 
              :key="member.id" 
              class="ranking-item"
            >
              <span class="ranking-number" :class="{ 'top-three': index < 3 }">{{ index + 1 }}</span>
              <span class="ranking-name">{{ member.name }}</span>
              <span class="ranking-value">{{ formatCurrency(member.totalSpent) }}</span>
            </div>
          </div>
          <div v-else class="empty-state">
            <i class="ri-inbox-line"></i>
            <p>暫無數據</p>
          </div>
        </div>

        <div class="ranking-card">
          <h3><i class="ri-coupon-line"></i> 優惠券效果排行</h3>
          <div v-if="stats.rankingData && stats.rankingData.topCoupons && stats.rankingData.topCoupons.length > 0" class="ranking-list">
            <div 
              v-for="(coupon, index) in stats.rankingData.topCoupons" 
              :key="coupon.id" 
              class="ranking-item"
            >
              <span class="ranking-number" :class="{ 'top-three': index < 3 }">{{ index + 1 }}</span>
              <span class="ranking-name">{{ coupon.couponName }}</span>
              <span class="ranking-value">{{ coupon.usageCount }} 次</span>
            </div>
          </div>
          <div v-else class="empty-state">
            <i class="ri-inbox-line"></i>
            <p>暫無數據</p>
          </div>
        </div>
      </div>

      <!-- 門市維度 + 活動成效 -->
      <div class="ranking-section">
        <div class="ranking-card">
          <div class="ranking-card-header">
            <h3><i class="ri-store-2-line"></i> 門市業績 Top 10（近30天）</h3>
            <button type="button" class="link-btn" @click="$router.push('/client/reports/sales')">查看報表</button>
          </div>
          <div v-if="topStores.length > 0" class="ranking-list">
            <div
              v-for="(store, index) in topStores"
              :key="store.id"
              class="ranking-item"
            >
              <span class="ranking-number" :class="{ 'top-three': index < 3 }">{{ index + 1 }}</span>
              <span class="ranking-name">{{ store.storeName }}</span>
              <span class="ranking-meta">{{ store.orderCount }} 單</span>
              <span class="ranking-value">{{ formatCurrency(store.totalRevenue) }}</span>
            </div>
          </div>
          <div v-else class="empty-state">
            <i class="ri-inbox-line"></i>
            <p>暫無門市業績</p>
          </div>
        </div>

        <div class="ranking-card">
          <div class="ranking-card-header">
            <h3><i class="ri-megaphone-line"></i> 活動成效</h3>
            <button type="button" class="link-btn" @click="$router.push({ name: 'ClientMarketingCampaignOverview' })">查看全部</button>
          </div>
          <div v-if="campaignLoading" class="empty-state">
            <p>載入中...</p>
          </div>
          <template v-else>
            <div v-if="campaignSummary" class="campaign-summary-row">
              <div class="campaign-summary-item">
                <span class="label">活動數</span>
                <strong>{{ formatNumber(campaignSummary.campaignCount) }}</strong>
              </div>
              <div class="campaign-summary-item">
                <span class="label">參與</span>
                <strong>{{ formatNumber(campaignSummary.participants) }}</strong>
              </div>
              <div class="campaign-summary-item">
                <span class="label">營收</span>
                <strong>{{ formatCurrency(campaignSummary.revenue) }}</strong>
              </div>
              <div class="campaign-summary-item">
                <span class="label">ROI</span>
                <strong>{{ formatRoi(campaignSummary.roi?.value) }}</strong>
              </div>
            </div>
            <div v-if="campaignItems.length > 0" class="ranking-list">
              <div
                v-for="(item, index) in campaignItems"
                :key="item.campaignId"
                class="ranking-item"
              >
                <span class="ranking-number" :class="{ 'top-three': index < 3 }">{{ index + 1 }}</span>
                <span class="ranking-name">{{ item.campaignName }}</span>
                <span class="ranking-meta">參與 {{ formatNumber(item.participants) }}</span>
                <span class="ranking-value">{{ formatCurrency(item.conversionRevenue) }}</span>
              </div>
            </div>
            <div v-else class="empty-state">
              <i class="ri-inbox-line"></i>
              <p>暫無活動成效</p>
            </div>
          </template>
        </div>
      </div>

      <!-- 快速功能區 -->
      <div class="dashboard-section">
        <h2>快速功能</h2>
        <div class="quick-actions">
          <button class="quick-action-btn" @click="$router.push('/client/members')">
            <i class="ri-user-line"></i>
            <span>會員管理</span>
          </button>
          <button class="quick-action-btn" @click="$router.push('/client/orders')">
            <i class="ri-shopping-cart-line"></i>
            <span>訂單管理</span>
          </button>
          <button class="quick-action-btn" @click="$router.push('/client/points/transactions')">
            <i class="ri-coins-line"></i>
            <span>點數管理</span>
          </button>
          <button class="quick-action-btn" @click="$router.push('/client/coupons/issues')">
            <i class="ri-coupon-line"></i>
            <span>優惠券管理</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import authService from '@/helpers/authservice/authService.js';
import dashboardApi from '@/api/modules/dashboard.js';
import { subscriptionService } from '@/api/modules/subscription.js';
import { useMenuStore } from '@/stores/menu.js';
import lineConfigApi from '@/api/modules/lineConfig.js';
import MultiTrendChart from '@/components/charts/MultiTrendChart.vue';
import PieChart from '@/components/charts/PieChart.vue';
import TierRedemptionChart from '@/components/charts/TierRedemptionChart.vue';
import { marketingCampaignService } from '@/api/modules/marketingCampaigns.js';

export default {
  name: 'ClientDashboard',
  components: {
    MultiTrendChart,
    PieChart,
    TierRedemptionChart
  },
  setup() {
    const router = useRouter();
    const menuStore = useMenuStore();

    const user = ref(null);
    const stats = ref(null);
    const loading = ref(false);
    const refreshing = ref(false);
    const error = ref(null);
    const lastUpdated = ref(null);
    const fromCache = ref(false);
    const showSubscriptionModal = ref(false);
    const subscriptionInfo = ref(null);
    const subscriptionLoaded = ref(false);

    const lineConfigs = ref([]);
    const lineConfigsLoading = ref(false);

    const periodOptions = [
      { value: 'day', label: '日' },
      { value: 'week', label: '週' },
      { value: 'month', label: '月' }
    ];
    const memberGrowthPeriod = ref('month');
    const opsTrendPeriod = ref('month');

    const campaignLoading = ref(false);
    const campaignSummary = ref(null);
    const campaignItems = ref([]);

    const GROWTH_NOTE = '成長率＝（本期 − 上期）÷ 上期 × 100%；上期為 0 時顯示 0%。「本月／上月」皆以日曆月計算。';
    const KPI_HELP_MAP = {
      members: {
        title: '會員總數',
        items: [
          { label: '主數字', text: '全部會員人數（含停用等非有效狀態）。' },
          { label: '有效會員', text: '狀態為 active 的會員人數。' },
          { label: '本月新增', text: '本月 1 日起至今新建立的有效會員數。' },
          { label: '較上月底', text: '目前有效會員數，對比「上月最後一天結束前」已存在的有效會員數。' }
        ],
        note: GROWTH_NOTE
      },
      activeMembers: {
        title: '活躍會員',
        items: [
          { label: '活躍會員', text: '本月內至少有一筆「已完成訂單」的不重複會員數。' },
          { label: '已完成訂單', text: '訂單狀態為已結案（closed），且物流為已取貨或已配達。' },
          { label: '活躍率', text: '活躍會員 ÷ 有效會員 × 100%。' },
          { label: '較上月', text: '本月活躍人數對比上月（完整日曆月）活躍人數。' }
        ],
        note: GROWTH_NOTE
      },
      couponsIssued: {
        title: '優惠券發行',
        items: [
          { label: '主數字', text: '優惠券發行紀錄（coupon_issues）累計張數。' },
          { label: '本月', text: '本月 1 日起至今建立的發行張數。' },
          { label: '較上月', text: '本月發行張數對比上月發行張數。' }
        ],
        note: GROWTH_NOTE
      },
      couponsUsed: {
        title: '優惠券核銷',
        items: [
          { label: '主數字', text: '狀態為已核銷（used）的累計張數。' },
          { label: '本月', text: '本月核銷時間（used_at）落在本月的張數。' },
          { label: '較上月', text: '本月核銷張數對比上月核銷張數。' }
        ],
        note: GROWTH_NOTE
      },
      usageRate: {
        title: '核銷率',
        items: [
          { label: '累計核銷率', text: '累計核銷張數 ÷ 累計發行張數 × 100%。' },
          { label: '本月核銷率', text: '本月核銷張數 ÷ 本月發行張數 × 100%（分母為 0 時為 0%）。' },
          { label: '較上月', text: '本月核銷率對比上月核銷率（上月＝上月核銷 ÷ 上月發行）。' }
        ],
        note: GROWTH_NOTE
      },
      pointsIssued: {
        title: '發放點數',
        items: [
          { label: '主數字', text: '點數交易類型為發放（earn）的點數加總。' },
          { label: '本月', text: '本月建立的發放點數加總。' },
          { label: '餘額', text: '目前有效會員可用點數（available_points）加總。' },
          { label: '較上月', text: '本月發放點數對比上月發放點數。' }
        ],
        note: GROWTH_NOTE
      },
      pointsUsed: {
        title: '消耗點數',
        items: [
          { label: '主數字', text: '點數交易類型為兌換／消耗（redeem）的點數絕對值加總。' },
          { label: '本月', text: '本月建立的消耗點數加總。' },
          { label: '較上月', text: '本月消耗點數對比上月消耗點數。' }
        ],
        note: GROWTH_NOTE
      },
      revenue: {
        title: '本月營收',
        items: [
          { label: '本月營收', text: '本月「已完成訂單」的訂單金額（total_amount）加總。' },
          { label: '已完成訂單', text: '訂單狀態為已結案（closed），且物流為已取貨或已配達。' },
          { label: '較上月', text: '本月營收對比上月（完整日曆月）營收。' }
        ],
        note: GROWTH_NOTE
      }
    };

    const kpiHelpKey = ref(null);
    const kpiHelp = computed(() => (kpiHelpKey.value ? KPI_HELP_MAP[kpiHelpKey.value] || null : null));
    const openKpiHelp = (key) => {
      kpiHelpKey.value = key;
    };
    const closeKpiHelp = () => {
      kpiHelpKey.value = null;
    };

    const hasWeeklyTrend = (trend) =>
      !!(trend && Array.isArray(trend.membersWeekly) && trend.membersWeekly.length > 0);

    const todayTaipeiKey = () => new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Asia/Taipei',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).format(new Date());

    const clipDailyToToday = (series) => {
      if (!Array.isArray(series) || !series.length) return null;
      const today = todayTaipeiKey();
      const clipped = series.filter((item) => {
        const key = typeof item?.date === 'string' ? item.date.slice(0, 10) : '';
        return /^\d{4}-\d{2}-\d{2}$/.test(key) && key <= today;
      });
      return clipped.length ? clipped : null;
    };

    const pickTrendSeries = (trend, baseKey, period) => {
      if (!trend) return null;
      // 嚴格依期間取對應序列，避免週缺資料時誤用月序列導致「看起來沒差」
      if (period === 'week') {
        const weekly = trend[`${baseKey}Weekly`];
        return Array.isArray(weekly) && weekly.length ? weekly : null;
      }
      if (period === 'month') {
        const monthly = trend[`${baseKey}Monthly`];
        return Array.isArray(monthly) && monthly.length ? monthly : null;
      }
      // day：軸日期不可超過今天（台北）
      const daily = trend[baseKey];
      if (Array.isArray(daily) && daily.length) return clipDailyToToday(daily);
      // followers 沒有日序列，日視圖不混入月序列（避免軸出現月份）
      return null;
    };

    const periodLabel = (period) => {
      if (period === 'day') return '近30天';
      if (period === 'week') return '近12週';
      return '近6個月';
    };

    const totalFollowers = computed(() =>
      lineConfigs.value.reduce((sum, c) => sum + (c.followersCount || 0), 0)
    );

    const loadLineConfigs = async () => {
      lineConfigsLoading.value = true;
      try {
        const res = await lineConfigApi.list({ pageSize: 100, isActive: true });
        if (res?.success && res?.data) {
          lineConfigs.value = res.data.items || res.data || [];
        }
      } catch (e) {
        console.error('[Dashboard] 載入 LINE 帳號失敗:', e.message);
      } finally {
        lineConfigsLoading.value = false;
        if (lineConfigs.value.length === 0 && !localStorage.getItem(getLineSetupKey())) {
          showLineSetupModal.value = true;
        }
      }
    };

    const inferPlanName = (quotaType, quotaValue) => {
      if (quotaType === 'none') return { name: '無上限', addon: false };
      if (!quotaValue) return null;
      if (quotaValue <= 200)  return { name: '輕用量', addon: false };
      if (quotaValue <= 3000) return { name: '中用量', addon: false };
      if (quotaValue === 6000) return { name: '高用量', addon: false };
      if (quotaValue > 6000)  return { name: '高用量', addon: true, addonCount: quotaValue - 6000 };
      return null;
    };

    const quotaUsagePct = (config) => {
      if (!config.messageQuotaValue || config.quotaConsumption == null) return 0;
      return Math.min(100, Math.round(config.quotaConsumption / config.messageQuotaValue * 100));
    };

    // 新手引導 Modal
    const showOnboardingModal = ref(false);
    const onboardingDismissChecked = ref(false);

    const getOnboardingKey = () => {
      const u = authService.getCurrentUser();
      return u?.id ? `onboarding_dismissed_${u.id}` : 'onboarding_dismissed';
    };

    const closeOnboardingModal = () => {
      if (onboardingDismissChecked.value) {
        localStorage.setItem(getOnboardingKey(), 'true');
      }
      showOnboardingModal.value = false;
    };

    const goOnboardingRoute = (routeName) => {
      closeOnboardingModal();
      router.push({ name: routeName });
    };

    const lineSetupNoticeDismissed = ref(false);

    const showLineSetupNotice = computed(() =>
      !lineConfigsLoading.value &&
      lineConfigs.value.length === 0 &&
      !lineSetupNoticeDismissed.value
    );

    const showLineSetupModal = ref(false);
    const lineSetupDismissChecked = ref(false);

    const getLineSetupKey = () => {
      const u = authService.getCurrentUser();
      return u?.id ? `line_setup_dismissed_${u.id}` : 'line_setup_dismissed';
    };

    const closeLineSetupModal = () => {
      if (lineSetupDismissChecked.value) {
        localStorage.setItem(getLineSetupKey(), 'true');
      }
      showLineSetupModal.value = false;
    };

    const handleLineSetupGo = () => {
      if (lineSetupDismissChecked.value) {
        localStorage.setItem(getLineSetupKey(), 'true');
      }
      showLineSetupModal.value = false;
      router.push({ name: 'ClientCreateLineConfig' });
    };

    const subscriptionError = computed(() => menuStore.subscriptionError);

    const MODAL_SESSION_KEY = 'subscription_modal_shown';

    const getSubscriptionIcon = (type) => {
      const icons = {
        expired: 'ri-close-circle-line',
        grace_period: 'ri-error-warning-line',
        expiring_soon: 'ri-time-line',
      };
      return icons[type] || 'ri-information-line';
    };

    const getSubscriptionTitle = (type) => {
      const titles = {
        expired: '訂閱已到期',
        grace_period: '訂閱進入寬限期',
        expiring_soon: '訂閱即將到期',
      };
      return titles[type] || '訂閱通知';
    };

    const closeSubscriptionModal = () => {
      showSubscriptionModal.value = false;
      sessionStorage.setItem(MODAL_SESSION_KEY, 'true');
    };

    const handleModalRenew = () => {
      closeSubscriptionModal();
      router.push('/client/subscription');
    };

    const hasAlerts = computed(() => {
      if (!stats.value) return false;
      return stats.value.orderStats.pendingOrders > 0 ||
             stats.value.couponStats.expiringSoon > 0 ||
             stats.value.pointsStats.expiringIn30Days > 0;
    });

    const opsTrendTitle = computed(() => `營運趨勢分析（${periodLabel(opsTrendPeriod.value)}）`);
    const memberGrowthTitle = computed(() => `會員成長趨勢（${periodLabel(memberGrowthPeriod.value)}）`);

    const opsTrendSeries = computed(() => {
      const trend = stats.value?.trendData;
      if (!trend) return [];
      const period = opsTrendPeriod.value;
      const series = [];
      const membersData = pickTrendSeries(trend, 'members', period);
      const couponsIssuedData = pickTrendSeries(trend, 'couponsIssued', period);
      const couponsUsedData = pickTrendSeries(trend, 'couponsUsed', period);
      const ordersData = pickTrendSeries(trend, 'orders', period);
      const revenueData = pickTrendSeries(trend, 'revenue', period);
      const followersData = pickTrendSeries(trend, 'followers', period);

      if (membersData) series.push({ name: '會員新增', color: '#667eea', data: membersData });
      if (couponsIssuedData) series.push({ name: '優惠券發行', color: '#9b59b6', data: couponsIssuedData });
      if (couponsUsedData) series.push({ name: '優惠券核銷', color: '#e67e22', data: couponsUsedData });
      if (ordersData) series.push({ name: '訂單數', color: '#feca57', data: ordersData, type: 'line' });
      if (followersData) series.push({ name: '有效好友數', color: '#06c755', data: followersData, type: 'line' });
      if (revenueData) {
        series.push({
          name: '營業額',
          color: '#4facfe',
          data: revenueData,
          type: 'bar',
          yAxisIndex: 1
        });
      }
      return series;
    });

    const memberGrowthSeries = computed(() => {
      const trend = stats.value?.trendData;
      if (!trend) return [];
      const period = memberGrowthPeriod.value;
      const series = [];
      const membersData = pickTrendSeries(trend, 'members', period);
      const followersData = pickTrendSeries(trend, 'followers', period);
      if (membersData) series.push({ name: '會員新增', color: '#667eea', data: membersData, type: 'line' });
      if (followersData) series.push({ name: '有效好友數', color: '#06c755', data: followersData, type: 'line' });
      return series;
    });

    const tierRedemptionData = computed(() => {
      const list = stats.value?.couponStats?.tierRedemption;
      return Array.isArray(list) ? list : [];
    });

    const topStores = computed(() => {
      const list = stats.value?.rankingData?.topStores;
      return Array.isArray(list) ? list : [];
    });

    const loadCampaignOverview = async () => {
      campaignLoading.value = true;
      try {
        const res = await marketingCampaignService.getAnalyticsOverview({
          page: 1,
          pageSize: 5,
          sortBy: 'revenue',
          order: 'desc'
        });
        if (res?.success && res?.data) {
          campaignSummary.value = res.data.summary || null;
          campaignItems.value = Array.isArray(res.data.items) ? res.data.items.slice(0, 5) : [];
        } else {
          campaignSummary.value = null;
          campaignItems.value = [];
        }
      } catch (e) {
        console.error('[Dashboard] 載入活動成效失敗:', e.message);
        campaignSummary.value = null;
        campaignItems.value = [];
      } finally {
        campaignLoading.value = false;
      }
    };

    const loadStats = async (refresh = false) => {
      try {
        if (refresh) {
          refreshing.value = true;
        } else {
          loading.value = true;
        }
        error.value = null;

        const response = await dashboardApi.getStats(refresh);
        
        if (response && response.success) {
          stats.value = response.data;
          lastUpdated.value = response.data?.lastUpdated;
          fromCache.value = response.data?.fromCache;

          // 舊快取沒有週序列時自動強制刷新一次，避免週／月看起來一樣
          if (!refresh && response.data?.fromCache && !hasWeeklyTrend(response.data?.trendData)) {
            await loadStats(true);
            return;
          }
        } else {
          error.value = response?.message || '載入統計數據失敗';
        }
      } catch (err) {
        console.error('載入 Dashboard 統計失敗:', err);
        error.value = err.response?.data?.message || '載入統計數據失敗，請稍後再試';
      } finally {
        loading.value = false;
        refreshing.value = false;
      }
    };

    const refreshStats = () => {
      loadStats(true);
    };

    const formatNumber = (num) => {
      if (num === undefined || num === null) return '0';
      return new Intl.NumberFormat('zh-TW').format(num);
    };

    const formatCurrency = (num) => {
      if (num === undefined || num === null) return '$0';
      return '$' + new Intl.NumberFormat('zh-TW').format(Math.round(num));
    };

    const formatDateTime = (dateString) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('zh-TW', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    };

    const getGrowthClass = (rate) => {
      if (rate > 0) return 'growth-positive';
      if (rate < 0) return 'growth-negative';
      return '';
    };

    const getGrowthIcon = (rate) => {
      if (rate > 0) return 'ri-arrow-up-line';
      if (rate < 0) return 'ri-arrow-down-line';
      return 'ri-subtract-line';
    };

    const formatGrowth = (rate) => {
      const n = Number(rate);
      if (!Number.isFinite(n) || n === 0) return '0%';
      const sign = n > 0 ? '+' : '';
      return `${sign}${n}%`;
    };

    // 與活動成效總覽一致：roi.value 為倍率，顯示為百分比
    const formatRoi = (value) => {
      if (value == null || value === '') return '—';
      const n = Number(value);
      if (!Number.isFinite(n)) return '—';
      return `${(n * 100).toFixed(1)}%`;
    };

    const formatDate = (dateString) => {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('zh-TW', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).format(date);
    };

    const subscriptionBadgeClass = computed(() => {
      if (subscriptionInfo.value?.neverExpires) return 'sub-badge--success';
      const status = subscriptionInfo.value?.status;
      if (!status) return 'sub-badge--default';
      if (status === 'expired') return 'sub-badge--danger';
      if (status === 'pending') return 'sub-badge--warning';
      if (status === 'grace_period') return 'sub-badge--warning';
      if (subscriptionInfo.value?.isExpiringSoon) return 'sub-badge--warn-soft';
      if (status === 'active' || status === 'trial') return 'sub-badge--success';
      return 'sub-badge--default';
    });

    const subscriptionBadgeIcon = computed(() => {
      if (subscriptionInfo.value?.neverExpires) return 'ri-infinity-line';
      const status = subscriptionInfo.value?.status;
      if (!status) return 'ri-file-unknown-line';
      if (status === 'expired') return 'ri-close-circle-line';
      if (status === 'grace_period') return 'ri-error-warning-line';
      if (subscriptionInfo.value?.isExpiringSoon) return 'ri-time-line';
      return 'ri-shield-check-line';
    });

    const loadSubscription = async () => {
      try {
        const res = await subscriptionService.getMySubscription();
        if (res && res.success) {
          subscriptionInfo.value = res.data?.subscription || null;
        }
      } catch (e) {
        // 靜默失敗，不影響主頁面
      } finally {
        subscriptionLoaded.value = true;
      }
    };

    onMounted(() => {
      user.value = authService.getCurrentUser();
      loadStats();
      loadSubscription();
      loadLineConfigs();
      loadCampaignOverview();

      // 新手引導：未曾勾選「不再顯示」則顯示
      if (!localStorage.getItem(getOnboardingKey())) {
        showOnboardingModal.value = true;
      }

      // LINE 橫幅：若已永久關閉則不顯示
      if (localStorage.getItem(getLineSetupKey())) {
        lineSetupNoticeDismissed.value = true;
      }

      // 若本 session 尚未顯示過訂閱警告 Modal，且有訂閱問題，則彈出
      if (menuStore.subscriptionError && !sessionStorage.getItem(MODAL_SESSION_KEY)) {
        showSubscriptionModal.value = true;
      }
    });

    return {
      user,
      stats,
      loading,
      refreshing,
      error,
      lastUpdated,
      fromCache,
      hasAlerts,
      periodOptions,
      memberGrowthPeriod,
      opsTrendPeriod,
      opsTrendTitle,
      memberGrowthTitle,
      opsTrendSeries,
      memberGrowthSeries,
      tierRedemptionData,
      topStores,
      campaignLoading,
      campaignSummary,
      campaignItems,
      kpiHelp,
      openKpiHelp,
      closeKpiHelp,
      subscriptionError,
      showSubscriptionModal,
      subscriptionInfo,
      subscriptionLoaded,
      subscriptionBadgeClass,
      subscriptionBadgeIcon,
      lineConfigs,
      lineConfigsLoading,
      totalFollowers,
      showOnboardingModal,
      onboardingDismissChecked,
      closeOnboardingModal,
      goOnboardingRoute,
      showLineSetupNotice,
      showLineSetupModal,
      lineSetupDismissChecked,
      closeLineSetupModal,
      handleLineSetupGo,
      loadStats,
      refreshStats,
      formatNumber,
      formatCurrency,
      formatDateTime,
      formatDate,
      getGrowthClass,
      getGrowthIcon,
      formatGrowth,
      formatRoi,
      getSubscriptionIcon,
      getSubscriptionTitle,
      closeSubscriptionModal,
      handleModalRenew,
      inferPlanName,
      quotaUsagePct,
    };
  },
};
</script>

<style scoped>
.dashboard-container {
  padding: 24px;
  min-height: 100vh;
  background: #f5f7fa;
}

.dashboard-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  background: white;
  padding: 12px 16px;
  border-radius: 10px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
}

.header-left {
  flex: 1;
  min-width: 0;
}

.dashboard-title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0 0 2px 0;
  line-height: 1.3;
}

.dashboard-subtitle {
  font-size: 13px;
  color: #666;
  margin: 0 0 2px 0;
}

.last-updated {
  font-size: 12px;
  color: #999;
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0;
}

.cache-badge {
  display: inline-block;
  padding: 2px 8px;
  background: #e3f2fd;
  color: #1976d2;
  border-radius: 4px;
  font-size: 12px;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

/* 訂閱到期日 Badge */
.sub-badge {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 16px;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;
  border: 1.5px solid transparent;
  white-space: nowrap;
  flex-shrink: 0;
}

.sub-badge:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.sub-badge i {
  font-size: 22px;
  flex-shrink: 0;
}

.sub-badge-text {
  display: flex;
  flex-direction: column;
  line-height: 1.3;
}

.sub-badge-label {
  font-size: 11px;
  opacity: 0.75;
  font-weight: 500;
}

.sub-badge-date {
  font-size: 14px;
  font-weight: 600;
}

.sub-badge-days {
  font-size: 12px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 20px;
  background: rgba(0, 0, 0, 0.08);
}

/* Badge 顏色狀態 */
.sub-badge--success {
  background: #f0fdf4;
  border-color: #86efac;
  color: #15803d;
}

.sub-badge--warn-soft {
  background: #eff6ff;
  border-color: #93c5fd;
  color: #1d4ed8;
}

.sub-badge--warning {
  background: #fff7ed;
  border-color: #fdba74;
  color: #c2410c;
}

.sub-badge--danger {
  background: #fff1f2;
  border-color: #fca5a5;
  color: #b91c1c;
}

.sub-badge--default {
  background: #f5f7fa;
  border-color: #e0e0e0;
  color: #555;
}

.subscribe-now-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.3s;
  animation: pulse-glow 2s infinite;
}

.subscribe-now-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(245, 87, 108, 0.45);
}

@keyframes pulse-glow {
  0%, 100% { box-shadow: 0 0 0 0 rgba(245, 87, 108, 0.4); }
  50% { box-shadow: 0 0 0 6px rgba(245, 87, 108, 0); }
}

.refresh-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s;
}

.refresh-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(79, 172, 254, 0.3);
}

.refresh-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.spinning {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.loading-container,
.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.loading-container i,
.error-container i {
  font-size: 48px;
  margin-bottom: 16px;
}

.loading-container i {
  color: #4facfe;
}

.error-container i {
  color: #f5576c;
}

.retry-btn {
  margin-top: 16px;
  padding: 8px 20px;
  background: #4facfe;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
}

.dashboard-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.stats-cards {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
}

.secondary-stats-row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.secondary-stat-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  font-size: 13px;
  color: #555;
}

.secondary-stat-chip i {
  font-size: 18px;
  color: #4facfe;
}

.secondary-stat-chip.line-chip {
  cursor: pointer;
  transition: box-shadow 0.2s;
}

.secondary-stat-chip.line-chip:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.secondary-stat-chip.line-chip i {
  color: #06c755;
}

.secondary-stat-chip strong {
  color: #333;
  font-weight: 600;
}

.secondary-stat-muted {
  color: #999;
  font-size: 12px;
}

.secondary-stat-muted.line-block {
  color: #e74c3c;
}

.stat-card {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  display: flex;
  align-items: center;
  gap: 20px;
  transition: all 0.3s;
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.12);
}

.stat-card--clickable {
  cursor: pointer;
}

.stat-icon {
  width: 64px;
  height: 64px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 28px;
  flex-shrink: 0;
}

.stat-content {
  flex: 1;
}

.stat-title-row {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 8px;
}

.stat-content h3 {
  font-size: 14px;
  color: #666;
  margin: 0;
  font-weight: 500;
}

.kpi-help-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: #9aa0a6;
  cursor: pointer;
  flex-shrink: 0;
  transition: color 0.15s, background 0.15s;
}

.kpi-help-btn:hover {
  color: #4facfe;
  background: #eef7ff;
}

.kpi-help-btn i {
  font-size: 15px;
  line-height: 1;
}

.kpi-help-modal {
  max-width: 480px;
}

.kpi-help-body {
  text-align: left;
}

.kpi-help-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.kpi-help-list li {
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 14px;
  color: #444;
  line-height: 1.5;
}

.kpi-help-list strong {
  font-size: 13px;
  color: #222;
  font-weight: 600;
}

.kpi-help-note {
  margin: 16px 0 0;
  padding: 10px 12px;
  border-radius: 8px;
  background: #f8f9fa;
  font-size: 12px;
  color: #666;
  line-height: 1.5;
}

.stat-value {
  font-size: 28px;
  font-weight: 600;
  color: #333;
  margin: 0 0 4px 0;
}

.stat-unit {
  font-size: 14px;
  font-weight: 500;
  color: #888;
  margin-left: 4px;
}

.stat-label {
  font-size: 13px;
  color: #999;
}

.stat-label-block {
  display: block;
  line-height: 1.6;
}

.line-followers-card {
  align-items: flex-start;
}

.line-configs-breakdown {
  margin-top: 6px;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.line-config-row {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #666;
}

.line-config-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #06c755;
  flex-shrink: 0;
}

.line-config-label {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.line-config-count {
  font-weight: 600;
  color: #333;
  flex-shrink: 0;
}

.line-config-block-rate {
  font-size: 11px;
  color: #e74c3c;
  flex-shrink: 0;
}

.line-single-stats {
  margin-top: 4px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.line-stat-item {
  font-size: 12px;
  color: #666;
}

.line-block-rate {
  color: #e74c3c;
}

.line-quota-section {
  margin-top: 4px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.line-quota-plan-row {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
  align-items: center;
}

.line-quota-badge {
  display: inline-block;
  border-radius: 3px;
  padding: 1px 6px;
  font-size: 11px;
  font-weight: 500;
}

.line-quota-badge.badge-primary  { background: #e3f2fd; color: #1565c0; }
.line-quota-badge.badge-info     { background: #e0f7fa; color: #00695c; }
.line-quota-badge.badge-success  { background: #e8f5e9; color: #2e7d32; }
.line-quota-badge.badge-secondary{ background: #f5f5f5; color: #616161; }
.line-quota-badge.badge-warning  { background: #fff3e0; color: #e65100; }

.line-quota-bar-wrap {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.line-quota-bar-track {
  height: 5px;
  background: #e0e0e0;
  border-radius: 3px;
  overflow: hidden;
  width: 100%;
  max-width: 160px;
}

.line-quota-bar-fill {
  height: 100%;
  border-radius: 3px;
  transition: width 0.3s ease;
}

.line-quota-bar-fill.fill-success { background: #27ae60; }
.line-quota-bar-fill.fill-warning { background: #f39c12; }
.line-quota-bar-fill.fill-danger  { background: #e74c3c; }

.line-quota-bar-label {
  font-size: 11px;
  color: #888;
}

.growth-positive {
  color: #15803d;
}

.growth-negative {
  color: #f5576c;
}

.alert-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.alert-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  border-radius: 8px;
  background: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.alert-card.warning {
  border-left: 4px solid #feca57;
}

.alert-card.info {
  border-left: 4px solid #4facfe;
}

.alert-card i {
  font-size: 20px;
  color: #feca57;
}

.alert-card.info i {
  color: #4facfe;
}

.alert-card span {
  flex: 1;
  color: #333;
}

.alert-btn {
  padding: 6px 16px;
  background: #feca57;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
}

.charts-section {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 20px;
}

.chart-card {
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  min-width: 0;
}

.chart-card--wide {
  min-height: 360px;
}

.chart-card--side {
  min-height: 360px;
}

.chart-empty {
  min-height: 280px;
}

.charts-section--single {
  grid-template-columns: 1fr;
}

.chart-card-toolbar {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 4px;
}

.period-tabs {
  display: inline-flex;
  gap: 4px;
  background: #f1f3f5;
  padding: 3px;
  border-radius: 8px;
}

.period-tab {
  border: none;
  background: transparent;
  padding: 4px 12px;
  border-radius: 6px;
  font-size: 12px;
  color: #666;
  cursor: pointer;
  line-height: 1.4;
}

.period-tab.active {
  background: #fff;
  color: #333;
  font-weight: 600;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
}

.ranking-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.ranking-card-header h3 {
  margin-bottom: 0;
}

.link-btn {
  border: none;
  background: transparent;
  color: #4facfe;
  font-size: 13px;
  cursor: pointer;
  padding: 0;
  white-space: nowrap;
}

.link-btn:hover {
  text-decoration: underline;
}

.ranking-meta {
  font-size: 12px;
  color: #999;
  margin-right: 8px;
  flex-shrink: 0;
}

.campaign-summary-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  margin-bottom: 14px;
}

.campaign-summary-item {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 10px 8px;
  text-align: center;
}

.campaign-summary-item .label {
  display: block;
  font-size: 11px;
  color: #999;
  margin-bottom: 4px;
}

.campaign-summary-item strong {
  font-size: 14px;
  color: #333;
}

.ranking-section {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 20px;
}

.ranking-card {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.ranking-card h3 {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.ranking-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.ranking-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
  transition: all 0.2s;
}

.ranking-item:hover {
  background: #e9ecef;
}

.ranking-number {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #dee2e6;
  color: #495057;
  font-weight: 600;
  font-size: 14px;
  flex-shrink: 0;
}

.ranking-number.top-three {
  background: linear-gradient(135deg, #feca57 0%, #ff9f43 100%);
  color: white;
}

.ranking-name {
  flex: 1;
  color: #333;
  font-weight: 500;
}

.ranking-value {
  color: #4facfe;
  font-weight: 600;
  font-size: 15px;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: #999;
}

.empty-state i {
  font-size: 48px;
  margin-bottom: 12px;
}

.dashboard-section {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.dashboard-section h2 {
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin-bottom: 20px;
}

.quick-actions {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
}

.quick-action-btn {
  padding: 20px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: white;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.quick-action-btn:hover {
  border-color: #4facfe;
  background: #f0f9ff;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(79, 172, 254, 0.2);
}

.quick-action-btn i {
  font-size: 32px;
  color: #4facfe;
}

.quick-action-btn span {
  font-size: 14px;
  font-weight: 500;
  color: #333;
}

/* 新手引導 Modal */
.onboarding-modal {
  background: white;
  border-radius: 18px;
  width: 100%;
  max-width: 560px;
  margin: 20px;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.18);
  overflow: hidden;
  animation: slideUpModal 0.25s ease;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

.onboarding-modal-header {
  padding: 24px 28px 20px;
  background: linear-gradient(135deg, #4facfe 0%, #667eea 100%);
  color: white;
}

.onboarding-modal-title-wrap {
  display: flex;
  align-items: center;
  gap: 16px;
}

.onboarding-modal-title-wrap > i {
  font-size: 36px;
  opacity: 0.95;
}

.onboarding-modal-title-wrap h3 {
  font-size: 20px;
  font-weight: 700;
  margin: 0 0 4px;
}

.onboarding-modal-title-wrap p {
  font-size: 13px;
  opacity: 0.85;
  margin: 0;
}

.onboarding-modal-body {
  padding: 20px 28px;
  overflow-y: auto;
  flex: 1;
}

.onboarding-steps {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.onboarding-step {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  border-radius: 10px;
  background: #f8f9fa;
  border: 1px solid #eee;
  transition: border-color 0.2s;
}

.onboarding-step:hover {
  border-color: #4facfe;
  background: #f0f9ff;
}

.onboarding-step-num {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, #4facfe 0%, #667eea 100%);
  color: white;
  font-size: 14px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.onboarding-step-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.onboarding-step-content strong {
  font-size: 14px;
  font-weight: 600;
  color: #222;
}

.onboarding-step-content span {
  font-size: 12px;
  color: #888;
  line-height: 1.4;
}

.onboarding-step-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 14px;
  background: white;
  border: 1.5px solid #4facfe;
  border-radius: 6px;
  color: #4facfe;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  transition: all 0.2s;
}

.onboarding-step-btn:hover {
  background: #4facfe;
  color: white;
}

.onboarding-modal-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 28px 20px;
  border-top: 1px solid #f0f0f0;
}

.onboarding-dismiss-check {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #666;
  cursor: pointer;
  user-select: none;
}

.onboarding-dismiss-check input[type="checkbox"] {
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: #4facfe;
}

.onboarding-close-btn {
  padding: 10px 24px;
  background: linear-gradient(135deg, #4facfe 0%, #667eea 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
}

.onboarding-close-btn:hover {
  opacity: 0.88;
  transform: translateY(-1px);
}

/* LINE 未設定 Modal 功能清單 */
.line-setup-modal-features {
  list-style: none;
  padding: 0;
  margin: 8px 0 4px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.line-setup-modal-features li {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  color: #333;
}

.line-setup-modal-features li i {
  font-size: 18px;
  color: #06c755;
  flex-shrink: 0;
}

/* LINE 未設定提示橫幅 */
.line-setup-notice {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px 24px;
  border-radius: 14px;
  background: linear-gradient(135deg, #e8f8ee 0%, #fff 70%);
  border: 1.5px solid #06c755;
  box-shadow: 0 4px 16px rgba(6, 199, 85, 0.12);
}

.line-setup-notice-icon {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: #06c755;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 26px;
  flex-shrink: 0;
}

.line-setup-notice-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.line-setup-notice-content strong {
  font-size: 16px;
  font-weight: 700;
  color: #065f32;
}

.line-setup-notice-content span {
  font-size: 13px;
  color: #3d7a52;
  line-height: 1.5;
}

.line-setup-notice-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 22px;
  background: #06c755;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  transition: all 0.2s;
}

.line-setup-notice-btn:hover {
  background: #05b04a;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(6, 199, 85, 0.35);
}

/* 訂閱警告 Inline Alert Card */
.subscription-alert-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 20px;
  border-radius: 12px;
  background: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border-left: 5px solid #feca57;
}

.subscription-alert-card--expiring_soon {
  border-left-color: #4facfe;
  background: linear-gradient(135deg, #f0f9ff 0%, #fff 60%);
}

.subscription-alert-card--grace_period {
  border-left-color: #ff9f43;
  background: linear-gradient(135deg, #fff8f0 0%, #fff 60%);
}

.subscription-alert-card--expired {
  border-left-color: #f5576c;
  background: linear-gradient(135deg, #fff0f3 0%, #fff 60%);
}

.subscription-alert-card-icon {
  font-size: 28px;
  flex-shrink: 0;
}

.subscription-alert-card--expiring_soon .subscription-alert-card-icon { color: #4facfe; }
.subscription-alert-card--grace_period .subscription-alert-card-icon { color: #ff9f43; }
.subscription-alert-card--expired .subscription-alert-card-icon { color: #f5576c; }

.subscription-alert-card-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.subscription-alert-card-content strong {
  font-size: 15px;
  font-weight: 600;
  color: #333;
}

.subscription-alert-card-content span {
  font-size: 13px;
  color: #666;
}

.subscription-alert-card-btn {
  padding: 8px 20px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: white;
  white-space: nowrap;
  flex-shrink: 0;
  transition: all 0.2s;
}

.subscription-alert-card--expiring_soon .subscription-alert-card-btn { background: #4facfe; }
.subscription-alert-card--grace_period .subscription-alert-card-btn { background: #ff9f43; }
.subscription-alert-card--expired .subscription-alert-card-btn { background: #f5576c; }

.subscription-alert-card-btn:hover {
  opacity: 0.88;
  transform: translateY(-1px);
}

/* 訂閱警告 Popup Modal */
.subscription-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  animation: fadeInOverlay 0.2s ease;
}

@keyframes fadeInOverlay {
  from { opacity: 0; }
  to { opacity: 1; }
}

.subscription-modal {
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 480px;
  margin: 20px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  animation: slideUpModal 0.25s ease;
}

@keyframes slideUpModal {
  from { transform: translateY(24px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}

.subscription-modal-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px 24px 16px;
  border-bottom: 1px solid #f0f0f0;
}

.subscription-modal-header i:first-child {
  font-size: 28px;
}

.subscription-modal--expiring_soon .subscription-modal-header i:first-child { color: #4facfe; }
.subscription-modal--grace_period .subscription-modal-header i:first-child { color: #ff9f43; }
.subscription-modal--expired .subscription-modal-header i:first-child { color: #f5576c; }

.subscription-modal-header h3 {
  flex: 1;
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.subscription-modal-close {
  width: 32px;
  height: 32px;
  border: none;
  background: #f5f5f5;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  color: #666;
  transition: background 0.2s;
}

.subscription-modal-close:hover {
  background: #e8e8e8;
}

.subscription-modal-body {
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.subscription-modal-body p {
  font-size: 15px;
  color: #444;
  line-height: 1.6;
  margin: 0;
}

.subscription-modal-hint {
  font-size: 13px !important;
  color: #888 !important;
}

.subscription-modal-footer {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding: 16px 24px 20px;
  border-top: 1px solid #f0f0f0;
}

.subscription-modal-btn {
  padding: 10px 22px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.subscription-modal-btn--primary {
  display: flex;
  align-items: center;
  gap: 6px;
  color: white;
}

.subscription-modal--expiring_soon .subscription-modal-btn--primary { background: #4facfe; }
.subscription-modal--grace_period .subscription-modal-btn--primary { background: #ff9f43; }
.subscription-modal--expired .subscription-modal-btn--primary { background: #f5576c; }

.subscription-modal-btn--primary:hover {
  opacity: 0.88;
  transform: translateY(-1px);
}

.subscription-modal-btn--secondary {
  background: #f5f5f5;
  color: #666;
}

.subscription-modal-btn--secondary:hover {
  background: #e8e8e8;
}

/* 響應式設計 */
@media (max-width: 1200px) {
  .stats-cards {
    grid-template-columns: repeat(2, 1fr);
  }

  .charts-section {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .dashboard-container {
    padding: 16px;
  }

  .dashboard-header {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
    padding: 12px;
  }

  .dashboard-title {
    font-size: 17px;
  }

  .header-right {
    flex-direction: column;
    align-items: stretch;
    width: 100%;
    gap: 8px;
  }

  .sub-badge {
    width: 100%;
    box-sizing: border-box;
    white-space: normal;
    overflow: hidden;
    flex-wrap: nowrap;
  }

  .sub-badge-text {
    flex: 1;
    min-width: 0;
    overflow: hidden;
  }

  .sub-badge-label {
    font-size: 10px;
  }

  .sub-badge-date {
    font-size: 13px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .sub-badge-days {
    flex-shrink: 0;
    white-space: nowrap;
    font-size: 11px;
  }

  .refresh-btn {
    width: 100%;
    justify-content: center;
  }

  .campaign-summary-row {
    grid-template-columns: repeat(2, 1fr);
  }

  .stats-cards {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }

  .stat-card {
    padding: 16px;
    gap: 12px;
  }

  .stat-icon {
    width: 48px;
    height: 48px;
    font-size: 22px;
  }

  .stat-value {
    font-size: 22px;
  }

  .secondary-stats-row {
    flex-direction: column;
  }

  .charts-section {
    grid-template-columns: 1fr;
  }

  .ranking-section {
    grid-template-columns: 1fr;
  }

  .quick-actions {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>
